"use client";

import { Check, Copy, Quote } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { writeToClipboardOrThrow } from "@/lib/clipboard";

interface CiteThisPageProps {
  title: string;
  url: string;
}

export function CiteThisPage({ title, url }: CiteThisPageProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedApa, setCopiedApa] = useState(false);
  const [copiedMla, setCopiedMla] = useState(false);

  const apaCitation = `CitationEasy. (2026). ${title}. CitationEasy. ${url}`;
  const mlaCitation = `"${title}." CitationEasy, 2026, ${url}.`;

  const copyToClipboard = async (text: string, format: "apa" | "mla") => {
    try {
      await writeToClipboardOrThrow(text);
      if (format === "apa") {
        setCopiedApa(true);
        setTimeout(() => {
          setCopiedApa(false);
        }, 2000);
      } else {
        setCopiedMla(true);
        setTimeout(() => {
          setCopiedMla(false);
        }, 2000);
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-border bg-muted/50 p-4">
      {isExpanded ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-semibold text-foreground text-sm">
              <Quote className="size-4" />
              Cite this page
            </h3>
            <Button
              onClick={() => {
                setIsExpanded(false);
              }}
              size="sm"
              variant="ghost"
            >
              Close
            </Button>
          </div>

          <div className="space-y-3">
            <div className="rounded-md border border-border bg-card p-3">
              <div className="mb-2 flex items-start justify-between gap-2">
                <span className="font-semibold text-muted-foreground text-xs">
                  APA Format
                </span>
                <Button
                  className="h-6 px-2"
                  onClick={() => {
                    copyToClipboard(apaCitation, "apa");
                  }}
                  size="sm"
                  variant="ghost"
                >
                  {copiedApa ? (
                    <Check className="size-3" />
                  ) : (
                    <Copy className="size-3" />
                  )}
                </Button>
              </div>
              <p className="break-words text-foreground/80 text-sm">
                {apaCitation}
              </p>
            </div>

            <div className="rounded-md border border-border bg-card p-3">
              <div className="mb-2 flex items-start justify-between gap-2">
                <span className="font-semibold text-muted-foreground text-xs">
                  MLA Format
                </span>
                <Button
                  className="h-6 px-2"
                  onClick={() => {
                    copyToClipboard(mlaCitation, "mla");
                  }}
                  size="sm"
                  variant="ghost"
                >
                  {copiedMla ? (
                    <Check className="size-3" />
                  ) : (
                    <Copy className="size-3" />
                  )}
                </Button>
              </div>
              <p className="break-words text-foreground/80 text-sm">
                {mlaCitation}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <Button
          className="flex w-full items-center justify-center gap-2"
          onClick={() => {
            setIsExpanded(true);
          }}
          variant="ghost"
        >
          <Quote className="size-4" />
          Cite this page
        </Button>
      )}
    </div>
  );
}
